import { Badge, Button } from '@hub/ui';
import { Download } from 'lucide-react';
import type { ViewPrimitive, BladeContext } from '../registry';
import { BladeLoading, BladeError, BladeDenied, BladeEmpty, forcedBladeState } from '../BladeStates';

// Document-viewer view-primitive (HUB-276) — a document frame: type/metadata
// header, a paged content surface, and a download affordance. Content layer,
// theme-adaptive. The actual page render (PDF/image via blobstore) is a later
// slice; this catalog version shows the frame + metadata + placeholder pages so
// the view type exists. Metadata comes from `descriptor.params` when present.

interface DocMeta {
  kind: string;
  name: string;
  pages: number;
  date: string;
  size: string;
}

function metaFor(descriptor: BladeContext['descriptor']): DocMeta {
  const p = descriptor.params;
  return {
    kind: p?.kind ?? 'EOB',
    name: p?.name ?? descriptor.title ?? 'Document',
    pages: Number(p?.pages ?? '3') || 1,
    date: p?.date ?? '2026-05-12',
    size: p?.size ?? '412 KB',
  };
}

function PagePlaceholder() {
  return (
    <div className="mx-auto w-full max-w-[520px] rounded-coronis border border-border bg-card p-6 shadow-xs">
      <div className="flex flex-col gap-2.5">
        <div className="h-3 w-1/3 rounded bg-muted" />
        <div className="h-2.5 w-full rounded bg-muted" />
        <div className="h-2.5 w-11/12 rounded bg-muted" />
        <div className="h-2.5 w-full rounded bg-muted" />
        <div className="h-2.5 w-2/3 rounded bg-muted" />
        <div className="mt-3 h-2.5 w-1/2 rounded bg-muted" />
        <div className="h-2.5 w-full rounded bg-muted" />
        <div className="h-2.5 w-5/6 rounded bg-muted" />
      </div>
    </div>
  );
}

function DocumentBody({ ctx }: { ctx: BladeContext }) {
  const meta = metaFor(ctx.descriptor);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{meta.kind}</Badge>
            <span className="truncate text-sm font-medium text-foreground">{meta.name}</span>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {meta.pages} page{meta.pages === 1 ? '' : 's'} · {meta.date} · {meta.size}
          </div>
        </div>
        <Button type="button" variant="outline" size="sm" className="shrink-0">
          <Download className="size-4" /> Download
        </Button>
      </div>

      <div className="flex flex-col items-center gap-3 rounded-coronis bg-muted/40 p-4">
        {Array.from({ length: Math.min(meta.pages, 3) }, (_, i) => (
          <PagePlaceholder key={i} />
        ))}
        {meta.pages > 3 && (
          <div className="text-xs text-muted-foreground">+ {meta.pages - 3} more pages</div>
        )}
      </div>
    </div>
  );
}

export const documentPrimitive: ViewPrimitive = {
  key: 'document',
  render: (ctx) => {
    const forced = forcedBladeState(ctx.descriptor);
    if (forced === 'loading') return <BladeLoading />;
    if (forced === 'error') return <BladeError />;
    if (forced === 'denied') return <BladeDenied description="This document contains restricted PHI." />;
    if (forced === 'empty' || !ctx.descriptor.entityId) {
      return <BladeEmpty title="No document selected" description="Choose a document to view it here." />;
    }
    return <DocumentBody ctx={ctx} />;
  },
};
